import React, { useState, useEffect, useRef } from 'react';
import { Pessoa } from '../../types/pessoa';
import { TipoContaId } from '../../types/financeiro';
import { pessoaBelongsToGroup, GROUPS_LIST, GroupFilterType } from '../../utils/pessoaGroupUtils';
import { isPessoaAtiva } from '../../services/pessoaService';
import { Search, X, Check, Building2, User, ChevronsUpDown, AlertCircle, Building, Users, Truck, Landmark, UserCheck } from 'lucide-react';

const ICON_MAP: Record<GroupFilterType, React.FC<{ className?: string }>> = {
  all: Users,
  fornecedores: Building,
  clientes: Building2,
  motoristas: UserCheck,
  transportadoras: Truck,
  bancos: Landmark,
  pj: Building2,
  pf: User,
};

interface PessoaSearchSelectProps {
  value: number;
  onChange: (pessoaId: number, pessoa?: Pessoa) => void;
  pessoas: Pessoa[];
  tipoConta?: TipoContaId;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
  className?: string;
  showGroupSelect?: boolean;
}

export const PessoaSearchSelect: React.FC<PessoaSearchSelectProps> = ({
  value,
  onChange,
  pessoas,
  tipoConta,
  disabled = false,
  required = false,
  label,
  placeholder,
  className = '',
  showGroupSelect = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Default selected group based on tipoConta
  const initialGroup: GroupFilterType = 
    tipoConta === TipoContaId.PAGAR ? 'fornecedores' : 
    tipoConta === TipoContaId.RECEBER ? 'clientes' : 'all';

  const [selectedGroup, setSelectedGroup] = useState<GroupFilterType>(initialGroup);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Selected person object
  const selectedPessoa = pessoas.find((p) => Number(p.id) === Number(value));


  // Auto-detect group if person is selected
  useEffect(() => {
    if (selectedPessoa) {
      if (pessoaBelongsToGroup(selectedPessoa, selectedGroup)) return;
      
      // Auto switch group to match selected person
      for (const grp of ['fornecedores', 'clientes', 'motoristas', 'transportadoras', 'bancos', 'pj', 'pf'] as GroupFilterType[]) {
        if (pessoaBelongsToGroup(selectedPessoa, grp)) {
          setSelectedGroup(grp);
          break;
        }
      }
    }
  }, [value]);

  // Sync group default when tipoConta changes
  useEffect(() => {
    if (!value) {
      if (tipoConta === TipoContaId.PAGAR) setSelectedGroup('fornecedores');
      else if (tipoConta === TipoContaId.RECEBER) setSelectedGroup('clientes');
    }
  }, [tipoConta]);

  // Focus search input when popover opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter persons list based on group & search term
  const filteredPessoas = pessoas.filter((p) => {
    // 0. Active Status Filter
    const isSelected = Number(p.id) === Number(value);
    if (!isSelected && !isPessoaAtiva(p)) {
      return false;
    }

    // 1. Group Filter
    if (!pessoaBelongsToGroup(p, selectedGroup)) {
      // Keep selected person visible even if filter changed
      if (!isSelected) return false;
    }

    // 2. Search Term Filter
    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase().trim();
    const rawDocSearch = term.replace(/\D/g, '');
    const rawDocPessoa = (p.documento || '').replace(/\D/g, '');

    const nameMatch = (p.nome || '').toLowerCase().includes(term);
    const fantasiaMatch = (p.nome_fantasia || '').toLowerCase().includes(term);
    const docMatch = (p.documento || '').toLowerCase().includes(term) || (rawDocSearch.length > 0 && rawDocPessoa.includes(rawDocSearch));
    const emailMatch = (p.email || '').toLowerCase().includes(term);
    const foneMatch = (p.fone || '').toLowerCase().includes(term);

    return nameMatch || fantasiaMatch || docMatch || emailMatch || foneMatch;
  });

  const handleSelect = (p: Pessoa) => {
    onChange(p.id, p);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(0, undefined);
    setSearchTerm('');
  };

  const fieldLabel = label || (tipoConta === TipoContaId.RECEBER ? 'Cliente' : tipoConta === TipoContaId.PAGAR ? 'Fornecedor' : 'Pessoa / Empresa');
  const defaultPlaceholder = tipoConta === TipoContaId.RECEBER 
    ? 'Clique para buscar o cliente...' 
    : tipoConta === TipoContaId.PAGAR 
    ? 'Clique para buscar o fornecedor...' 
    : 'Clique para selecionar...';

  return (
    <div className={`space-y-2.5 ${className}`} ref={containerRef}>
      {/* 1. Group Selector Field */}
      {showGroupSelect && (
        <div>
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 flex items-center gap-1.5">
            <span className="bg-red-100 text-red-700 w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] font-bold">1</span>
            Filtro de Grupo / Categoria
          </label>
          <select
            disabled={disabled}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white hover:border-slate-400 focus:border-red-500 focus:ring-1 focus:ring-red-500 font-medium text-slate-800 transition-colors disabled:bg-slate-100 disabled:text-slate-500"
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(e.target.value as GroupFilterType)}
          >
            {GROUPS_LIST.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 2. Person Searchable Selector */}
      <div className="relative">
        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 flex items-center gap-1.5">
          {showGroupSelect && (
            <span className="bg-red-100 text-red-700 w-4 h-4 rounded-full inline-flex items-center justify-center text-[10px] font-bold">2</span>
          )}
          {fieldLabel} {required && <span className="text-red-500">*</span>}
        </label>

        {/* Selected Box Trigger Button */}
        <div
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm transition-all cursor-pointer ${
            disabled
              ? 'bg-slate-100 border-slate-300 text-slate-500 cursor-not-allowed'
              : isOpen
              ? 'border-red-500 ring-2 ring-red-500/20 bg-white shadow-sm'
              : 'border-slate-300 hover:border-slate-400 bg-white'
          }`}
        >
          <div className="flex items-center gap-2.5 overflow-hidden pr-2">
            {selectedPessoa ? (
              <>
                {Number(selectedPessoa.tipo_cliente_id) === 2 || (selectedPessoa.documento && selectedPessoa.documento.replace(/\D/g, '').length > 11) ? (
                  <Building2 className="w-4 h-4 text-slate-500 shrink-0" />
                ) : (
                  <User className="w-4 h-4 text-slate-500 shrink-0" />
                )}
                <div className="truncate text-left">
                  <span className="font-semibold text-slate-800 block truncate leading-tight">
                    {selectedPessoa.nome}
                  </span>
                  {selectedPessoa.documento && (
                    <span className="text-[11px] text-slate-500 block">
                      Doc: {selectedPessoa.documento}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <span className="text-slate-400 truncate">
                {placeholder || defaultPlaceholder}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {selectedPessoa && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 rounded-full text-slate-400 hover:text-red-600 hover:bg-slate-100 transition-colors"
                title="Limpar seleção"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <ChevronsUpDown className="w-4 h-4 text-slate-400" />
          </div>
        </div>

        {/* Dropdown Popover */}
        {isOpen && (
          <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-xl bg-white border border-slate-200 shadow-xl overflow-hidden flex flex-col max-h-[400px] animate-in fade-in duration-150">
            {/* Header: Quick Search & Group Switcher */}
            <div className="p-2.5 bg-slate-50 border-b border-slate-200 space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="w-full pl-9 pr-8 py-2 text-xs sm:text-sm bg-white border border-slate-300 rounded-lg outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500 text-slate-800 placeholder-slate-400"
                  placeholder="Pesquisar por nome, razão social ou CPF/CNPJ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Group Quick Filter Pills inside popover */}
              <div className="flex items-center gap-1 overflow-x-auto pb-1 pt-0.5 scrollbar-thin">
                {GROUPS_LIST.map((grp) => {
                  const isActive = selectedGroup === grp.id;
                  const Icon = ICON_MAP[grp.id] || Users;
                  return (
                    <button
                      key={grp.id}
                      type="button"
                      onClick={() => setSelectedGroup(grp.id)}
                      className={`px-2 py-1 text-[11px] rounded-md font-semibold whitespace-nowrap transition-all flex items-center gap-1 cursor-pointer ${
                        isActive
                          ? 'bg-slate-800 text-white shadow-xs'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      <span>{grp.label.split('&')[0].trim()}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Results List */}
            <div className="overflow-y-auto flex-1 divide-y divide-slate-100 p-1">
              {filteredPessoas.length === 0 ? (
                <div className="p-6 text-center space-y-2">
                  <AlertCircle className="w-6 h-6 text-slate-400 mx-auto" />
                  <p className="text-xs font-medium text-slate-600">
                    Nenhum cadastro encontrado para este filtro ou busca.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedGroup('all');
                    }}
                    className="text-xs text-red-600 hover:underline font-semibold"
                  >
                    Mostrar todos os cadastros
                  </button>
                </div>
              ) : (
                filteredPessoas.slice(0, 70).map((p) => {
                  const isSelected = Number(p.id) === Number(value);
                  const isPj = Number(p.tipo_cliente_id) === 2 || (p.documento && p.documento.replace(/\D/g, '').length > 11);

                  return (
                    <div
                      key={p.id}
                      onClick={() => handleSelect(p)}
                      className={`p-2.5 rounded-lg flex items-center justify-between gap-3 cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-red-50 text-red-900 font-medium'
                          : 'hover:bg-slate-100 text-slate-800'
                      }`}
                    >
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className={`p-1.5 rounded-md shrink-0 mt-0.5 ${isSelected ? 'bg-red-200/60 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
                          {isPj ? <Building2 className="w-3.5 h-3.5" /> : <User className="w-3.5 h-3.5" />}
                        </div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-bold text-slate-900 truncate">
                              {p.nome}
                            </span>
                            {p.nome_fantasia && (
                              <span className="text-[10px] text-slate-500 italic truncate">
                                ({p.nome_fantasia})
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5 flex-wrap">
                            {p.documento && (
                              <span className="font-mono bg-slate-100 px-1.5 py-0.2 rounded text-[10px] text-slate-600">
                                {p.documento}
                              </span>
                            )}
                            {isPj ? (
                              <span className="text-[9px] bg-blue-50 text-blue-700 px-1 py-0.2 rounded font-semibold">
                                PJ / Empresa
                              </span>
                            ) : (
                              <span className="text-[9px] bg-emerald-50 text-emerald-700 px-1 py-0.2 rounded font-semibold">
                                PF
                              </span>
                            )}
                            {p.fone && <span>Tel: {p.fone}</span>}
                          </div>
                        </div>
                      </div>

                      {isSelected && (
                        <Check className="w-4 h-4 text-red-600 shrink-0 ml-2" />
                      )}
                    </div>
                  );
                })
              )}

              {filteredPessoas.length > 70 && (
                <div className="p-2 text-center text-[11px] text-slate-400 bg-slate-50">
                  Exibindo 70 resultados. Refine com a busca por nome ou CNPJ/CPF se necessário.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
